
import { FullRightAccess, ViewRightType, hasGlobalRightAccess } from "@dto/Users";
import { ObjectInfos } from "@dto/objects";
import { View } from "@dto/views";
import { isViewFolderCollapsed, setViewFolderCollapsed, updateStorageSelection } from "@utils/localStorage";
import { toastError } from "@utils/toasts";
import { TW_RootDropZone, TW_ViewList } from "@utils/twClasses";
import { ancestorsOf, buildFolderTree, folderOfView, moveFolder, moveViewToFolder, parentOf } from "@utils/viewFolders";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore, useUserStore } from "src/store";
import DraggableViewListItem from "./DraggableViewListItem";
import NewViewButton from "./NewViewButton";
import ViewFolderItem, { FOLDER_DRAG_TYPE, FolderMoveAnchor, VIEW_DRAG_TYPE } from "./ViewFolderItem";
import { useViewFolders } from "./useViewFolders";

interface ViewListProps {
  objectInfos: ObjectInfos;
  views: View[];
  onMobileClose?: () => void;
  showUserViews?: boolean;
}

function ViewList({ objectInfos, views, onMobileClose, showUserViews }: ViewListProps) {

  const { t } = useTranslation();
  const updateObjectInfos = useModelStore(state => state.updateObjectInfos);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const allObjectInfos = useModelStore(state => state.objectInfos);
  const userRole = useModelStore(state => state.userRole);
  const selectedView = useModelStore(state => state.selectedView);
  const user = useUserStore(state => state.user);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const { prefs, saveFolders } = useViewFolders(objectInfos.object._key);
  // Sort views by order to ensure correct display order
  const sortedViews = views ? [...views].sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
  const [draggedViewKey, setDraggedViewKey] = useState<string | null>(null);
  const [draggedFolderPath, setDraggedFolderPath] = useState<string | null>(null);
  const [isRootDropOver, setIsRootDropOver] = useState(false);
  const [dropLineState, setDropLineState] = useState<{
    viewKey: string;
    position: 'top' | 'bottom';
  } | null>(null);
  // Collapsed folder paths, persisted per workspace/object/path
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());

  const objectKey = objectInfos.object._key;
  const folderPathsKey = (prefs?.viewFolders ?? []).join("|");
  useEffect(() => {
    const collapsed = new Set<string>();
    (prefs?.viewFolders ?? []).forEach(path => {
      if (isViewFolderCollapsed(workspace?._key, objectKey, path)) {
        collapsed.add(path);
      }
    });
    setCollapsedPaths(collapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderPathsKey, workspace?._key, objectKey]);

  // Auto-expand the folder chain of the selected view (on selection change
  // only, so a manual collapse is not fought)
  useEffect(() => {
    if (!selectedView) {
      return;
    }
    const path = folderOfView(prefs, selectedView._key);
    if (!path) {
      return;
    }
    const chain = [...ancestorsOf(path), path];
    setCollapsedPaths(prev => {
      const toExpand = chain.filter(p => prev.has(p));
      if (toExpand.length === 0) {
        return prev;
      }
      toExpand.forEach(p => setViewFolderCollapsed(workspace?._key, objectKey, p, false));
      const next = new Set(prev);
      toExpand.forEach(p => next.delete(p));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView?._key]);

  const toggleFolderCollapsed = (path: string) => {
    const collapsed = !collapsedPaths.has(path);
    setViewFolderCollapsed(workspace?._key, objectKey, path, collapsed);
    setCollapsedPaths(prev => {
      const next = new Set(prev);
      if (collapsed) {
        next.add(path);
      } else {
        next.delete(path);
      }
      return next;
    });
  };

  const viewAdded = (view: View) => {
    const newViews = [...(sortedViews || []), view];
    const newObjectInfo = { ...objectInfos, views: newViews };
    const newAllObjectInfos = allObjectInfos.map((o: ObjectInfos) => {
      if (o.object._key === objectInfos.object._key) {
        o = newObjectInfo;
      }
      return o;
    });
    updateObjectInfos(newAllObjectInfos, newObjectInfo, view, null, sharedObjectInfos);
    updateStorageSelection(workspace?._key, newObjectInfo.object?._key, view?._key, null);
  }

  const handleViewOrderUpdate = (updatedView: View) => {
    const newViews = sortedViews.map(v => v._key === updatedView._key ? updatedView : v);
    // Sort views by order to ensure correct display order
    const reSortedViews = newViews.sort((a, b) => (a.order || 0) - (b.order || 0));
    const newObjectInfo = { ...objectInfos, views: reSortedViews };
    const newAllObjectInfos = allObjectInfos.map((o: ObjectInfos) => {
      if (o.object._key === objectInfos.object._key) {
        o = newObjectInfo;
      }
      return o;
    });
    if (selectedView?._key === updatedView._key) {
      selectedView.order = updatedView.order;
    }
    updateObjectInfos(newAllObjectInfos, newObjectInfo, selectedView, null, sharedObjectInfos);
  };

  // Files a view into a folder (null = back to the root), appended at the
  // end of the folder's personal order
  const handleAssignView = (viewKey: string, path: string | null) => {
    if (folderOfView(prefs, viewKey) === path) {
      return;
    }
    saveFolders(moveViewToFolder(prefs, viewKey, path));
  };

  // Drop between two views of a folder: pure personal reorder (splice in the
  // folder's list) — no write on the shared view order, no right required
  const handleDropInFolder = (draggedViewKey: string, targetViewKey: string, containerPath: string, position: 'top' | 'bottom') => {
    let beforeViewKey: string | null = targetViewKey;
    if (position === 'bottom') {
      const list = prefs?.viewsByFolder?.[containerPath] ?? [];
      const idx = list.indexOf(targetViewKey);
      beforeViewKey = idx >= 0 && idx + 1 < list.length ? list[idx + 1] : null;
    }
    saveFolders(moveViewToFolder(prefs, draggedViewKey, containerPath, beforeViewKey));
  };

  // A view dropped among the root views leaves its folder (its position at
  // the root is ruled by the shared view order)
  const handleMovedToRoot = (viewKey: string) => {
    if (folderOfView(prefs, viewKey) !== null) {
      saveFolders(moveViewToFolder(prefs, viewKey, null));
    }
  };

  // Moves a folder (whole subtree) under destParentPath, anchored among its
  // new siblings; an invalid move (own subtree, name conflict, too deep) is
  // refused with a toast
  const handleMoveFolder = (srcPath: string, destParentPath: string | null, anchor: FolderMoveAnchor | null) => {
    if (srcPath === destParentPath) {
      return;
    }
    let beforePath: string | null = null;
    if (anchor?.before) {
      beforePath = anchor.before;
    } else if (anchor?.after) {
      const siblings = (prefs?.viewFolders ?? []).filter(p => parentOf(p) === destParentPath && p !== srcPath);
      const idx = siblings.indexOf(anchor.after);
      beforePath = idx >= 0 && idx + 1 < siblings.length ? siblings[idx + 1] : null;
    }
    const moved = moveFolder(prefs, srcPath, destParentPath, beforePath);
    if (!moved) {
      toastError(t("folder.move.invalid.toast"));
      return;
    }
    saveFolders(moved);
  };

  const handleDragStart = (viewKey: string) => {
    setDraggedViewKey(viewKey);
  };

  const handleDragEnd = () => {
    setDraggedViewKey(null);
    setDropLineState(null);
    setIsRootDropOver(false);
  };

  const handleFolderDragStart = (path: string) => {
    setDraggedFolderPath(path);
  };

  const handleFolderDragEnd = () => {
    setDraggedFolderPath(null);
    setIsRootDropOver(false);
  };

  const handleDragOver = (viewKey: string, position: 'top' | 'bottom' | null) => {
    if (position && draggedViewKey && draggedViewKey !== viewKey) {
      setDropLineState({ viewKey, position });
    } else {
      setDropLineState(null);
    }
  };

  // Root drop zone, shown while dragging: sends a view or a folder back to
  // the root level
  const handleRootDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsRootDropOver(false);
    const viewKey = e.dataTransfer.getData(VIEW_DRAG_TYPE);
    if (viewKey) {
      handleAssignView(viewKey, null);
      handleDragEnd();
      return;
    }
    const folderPath = e.dataTransfer.getData(FOLDER_DRAG_TYPE);
    if (folderPath) {
      handleFolderDragEnd();
      if (parentOf(folderPath) !== null) {
        handleMoveFolder(folderPath, null, null);
      }
    }
  };

  const userKey = user?._key;
  const isAdmin = user?.owner;
  const hasViewFullRight = hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess);

  // Reordering the root views writes the shared view order: while dragging a
  // view this user cannot reorder, the root rows refuse the drop (folder
  // headers and the root zone stay available for the personal filing)
  const draggedView = draggedViewKey ? sortedViews.find(v => v._key === draggedViewKey) : undefined;
  const draggedViewReorderable = !draggedView
    || draggedView.ownerKey === userKey
    || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, draggedView._key);

  // Admin eye filter first, then the personal folder grouping; the views
  // masked by the eye also prune the folders that only "look" empty
  const isViewVisible = (view: View) => !isAdmin || view.ownerKey === userKey || showUserViews;
  const visibleViews = sortedViews.filter(isViewVisible);
  const hiddenViewKeys = new Set(sortedViews.filter(view => !isViewVisible(view)).map(view => view._key));
  const tree = buildFolderTree(prefs, visibleViews, hiddenViewKeys);

  const renderViewItem = (view: View, containerPath: string | null) => (
    <DraggableViewListItem
      key={view._key}
      object={objectInfos}
      view={view}
      containerFolderId={containerPath}
      draggedViewReorderable={draggedViewReorderable}
      onDropInFolder={handleDropInFolder}
      onMovedToRoot={handleMovedToRoot}
      onMobileClose={onMobileClose}
      onViewOrderUpdate={handleViewOrderUpdate}
      isDragging={draggedViewKey === view._key}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      showDropLineTop={dropLineState?.viewKey === view._key && dropLineState?.position === 'top'}
      showDropLineBottom={dropLineState?.viewKey === view._key && dropLineState?.position === 'bottom'}
    />
  );

  const isDragging = draggedViewKey !== null || draggedFolderPath !== null;
  // The root zone only accepts an item that actually lives in a folder:
  // dropping something already at the root would be a no-op, so the zone is
  // grayed out and refuses the drop
  const rootZoneActive = draggedViewKey !== null
    ? folderOfView(prefs, draggedViewKey) !== null
    : draggedFolderPath !== null && parentOf(draggedFolderPath) !== null;
  return (
    <>
      {(tree.rootFolders.length > 0 || visibleViews.length > 0) && (
        <div className={TW_ViewList}>
          {tree.rootFolders.map(node => (
            <ViewFolderItem
              key={node.path}
              objectInfos={objectInfos}
              node={node}
              collapsedPaths={collapsedPaths}
              onToggleCollapsed={toggleFolderCollapsed}
              draggedFolderPath={draggedFolderPath}
              onFolderDragStart={handleFolderDragStart}
              onFolderDragEnd={handleFolderDragEnd}
              onAssignView={handleAssignView}
              onMoveFolder={handleMoveFolder}
              renderViewItem={renderViewItem}
            />
          ))}
          {tree.rootViews.map(view => renderViewItem(view, null))}
          {isDragging && (
            <div
              className={TW_RootDropZone
                + (rootZoneActive ? "" : " opacity-50 cursor-not-allowed")
                + (rootZoneActive && isRootDropOver ? " border-gray-500 text-gray-600" : "")}
              onDragOver={(e) => {
                if (!rootZoneActive) {
                  // No preventDefault: the browser shows the not-allowed cursor
                  return;
                }
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setIsRootDropOver(true);
              }}
              onDragLeave={() => setIsRootDropOver(false)}
              onDrop={handleRootDrop}
            >
              {t("folder.root.text")}
            </div>
          )}
        </div>
      )}
      {hasViewFullRight && <NewViewButton objectInfos={objectInfos} onNewView={viewAdded} />}
    </>
  )
}

export default ViewList;
